import {
  filledInputClasses,
  inputBaseClasses,
  inputLabelClasses,
  outlinedInputClasses,
  TextFieldProps,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';

export default function TextField(theme: Theme) {
  const color = {
    focused: theme.palette.text.primary,
    active: theme.palette.text.secondary,
    placeholder: theme.palette.text.disabled,
  };

  const font = {
    label: theme.typography.body1,
    value: theme.typography.body2,
  };

  const rootStyles = (ownerState: TextFieldProps) => {
    const xsmallSize = ownerState.size === 'xsmall';
    const smallSize = ownerState.size === 'small';
    const mediumSize = ownerState.size === 'medium';
    const largeSize = ownerState.size === 'large';

    const { disabled, error, focused, multiline } = ownerState;

    const baseHeight = xsmallSize
      ? 24
      : smallSize
      ? 32
      : mediumSize
      ? 40
      : largeSize
      ? 48
      : 40;

    const padding = xsmallSize
      ? theme.spacing(0.5)
      : smallSize
      ? theme.spacing(1)
      : mediumSize
      ? theme.spacing(1, 2)
      : largeSize
      ? theme.spacing(1, 2)
      : theme.spacing(1, 2);

    const styles = {
      padding: multiline ? 0 : padding,
      height: multiline ? 'auto' : baseHeight,
      ...(multiline && {
        minHeight: baseHeight,
        alignItems: 'flex-start',
        '& > textarea': {
          padding,
        },
      }),
      fontSize: xsmallSize
        ? 12
        : smallSize
        ? 14
        : mediumSize
        ? 16
        : largeSize
        ? 16
        : 16,
      color: theme.palette.grey[600],
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: theme.palette.grey[300],
      borderRadius: xsmallSize
        ? theme.spacing(0.5)
        : smallSize
        ? theme.spacing(0.5)
        : mediumSize
        ? theme.spacing(1)
        : largeSize
        ? theme.spacing(1)
        : theme.spacing(1),
      gap: theme.spacing(0.5),
      ...(disabled && {
        backgroundColor: theme.palette.action.disabledBackground,
        boxShadow: `0 0 0 1px ${theme.palette.grey[300]}!important`,
      }),
      ...(error && {
        borderColor: theme.palette.error[500],
      }),
      ...(error &&
        focused && {
          boxShadow: `0 0 0 1px ${theme.palette.error[500]}!important`,
        }),
      ...(!error &&
        focused && {
          borderColor: color.focused,
        }),

      [`& > .MuiInputAdornment-root`]: {
        margin: 0,
      },

      '& > input': {
        padding: 0,
      },
      '& > input::placeholder, & > textarea::placeholder': {
        color: `${theme.palette.grey[400]}!important`,
      },
    };

    return styles;
  };

  return {
    MuiFormControl: {
      styleOverrides: {
        root: {
          gap: theme.spacing(0.5),
        },
      },
    },

    // HELPER
    MuiFormHelperText: {
      defaultProps: {
        component: 'div',
      },
      styleOverrides: {
        root: {
          margin: 0,
          // marginTop: theme.spacing(1),
        },
      },
    },

    // LABEL
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...font.value,
          color: color.placeholder,
          [`&.${inputLabelClasses.shrink}`]: {
            // ...font.label,
            // fontWeight: 600,
            // color: color.active,
            [`&.${inputLabelClasses.focused}`]: {
              // color: color.focused,
            },
            [`&.${inputLabelClasses.error}`]: {
              color: theme.palette.error[500],
            },
            [`&.${inputLabelClasses.disabled}`]: {
              color: theme.palette.text.disabled,
            },
            [`&.${inputLabelClasses.filled}`]: {
              transform: 'translate(12px, 6px) scale(0.75)',
            },
          },
        },
      },
    },

    // BASE
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.common.white,
          [`&.${inputBaseClasses.multiline}`]: {
            padding: 0,
          },
          [`&.${inputBaseClasses.disabled}`]: {
            '& svg': {
              color: theme.palette.text.disabled,
            },
          },
          // [`&.${inputBaseClasses.sizeSmall}`]: {
          //   fontSize: theme.typography.body5.fontSize,
          // },
        },
        input: {
          '&::placeholder': {
            opacity: 1,
            color: color.placeholder,
          },
        },
      },
    },

    // STANDARD
    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: alpha(theme.palette.grey[500], 0.32),
          },
          '&:after': {
            borderBottomColor: color.focused,
          },
        },
      },
    },

    // OUTLINED
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: TextFieldProps }) => ({
          ...rootStyles(ownerState),
          [`&.${outlinedInputClasses.focused}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: color.focused,
            },
          },
          [`&.${outlinedInputClasses.error}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.error[500],
            },
          },
          [`&.${outlinedInputClasses.disabled}`]: {
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.action.disabledBackground,
            },
          },
        }),
        notchedOutline: {
          display: 'none',
          borderColor: color.focused,
          // borderColor: alpha(theme.palette.grey[500], 0.2),
          // transition: theme.transitions.create(['border-color'], {
          //   duration: theme.transitions.duration.shortest,
          // }),
        },
      },
    },

    // FILLED
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.16),
          },
          [`&.${filledInputClasses.focused}`]: {
            backgroundColor: alpha(theme.palette.grey[500], 0.16),
          },
          [`&.${filledInputClasses.error}`]: {
            backgroundColor: alpha(theme.palette.error[500], 0.08),
            [`&.${filledInputClasses.focused}`]: {
              backgroundColor: alpha(theme.palette.error[500], 0.16),
            },
          },
          [`&.${filledInputClasses.disabled}`]: {
            backgroundColor: theme.palette.action.disabledBackground,
          },
        },
      },
    },
  };
}
